import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[epcNumberAnimate]',
  exportAs: 'epcNumberAnimate',
  standalone: false,
})
export class NumberAnimateDirective implements OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private counter$ = new Subject<void>();
  private startTimestamp: number | null = null;
  private currentAnimation = 0;
  private duration = 1000;
  private counter = 0;

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private renderer: Renderer2,
  ) {
    this.counter$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.animateValue();
    });
  }

  @Input('epcNumberAnimate')
  public value = 0;

  @Input()
  public transformer?: (value: number) => string;

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const valueChange = changes.value;

    if (valueChange) {
      this.counter$.next();
    }
  }

  private animateValue() {
    if (this.value > 0) {
      if (this.currentAnimation) {
        window.cancelAnimationFrame(this.currentAnimation);
      }

      this.startTimestamp = null;
      this.ngZone.runOutsideAngular(() => {
        this.currentAnimation = window.requestAnimationFrame(
          (timestamp: number) => this.step(timestamp),
        );
      });
    } else this.renderCounter(this.value);
  }

  private step(timestamp: number) {
    if (!this.startTimestamp) {
      this.startTimestamp = timestamp;
    }
    const progress = Math.min(
      (timestamp - this.startTimestamp) / this.duration,
      1,
    );

    this.counter = Math.floor(
      progress * (this.value - this.counter) + this.counter,
    );

    this.renderCounter(this.counter);

    if (progress < 1) {
      this.ngZone.runOutsideAngular(() => {
        window.requestAnimationFrame((t: number) => this.step(t));
      });
    }
  }

  private getStringValue(value: number) {
    return this.transformer ? this.transformer(value) : value;
  }

  private renderCounter(counter: number) {
    this.renderer.setProperty(
      this.elementRef.nativeElement,
      'innerHTML',
      this.getStringValue(counter),
    );
  }
}
